import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { photoSrc } from "../components/PropertyCard";
import FavouriteButton from "../components/FavouriteButton";
import { MapPin, Clock, Users, KeyRound, ShieldCheck, Wifi, Coffee, Car, Waves, Utensils, BadgeCheck, Star, MessageSquare, Crown } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const paymentLabels = {
  mtn: { name: "MTN Mobile Money", color: "bg-yellow-400 text-slate-900" },
  airtel: { name: "Airtel Money", color: "bg-red-500 text-white" },
  zamtel: { name: "Zamtel Kwacha", color: "bg-green-600 text-white" },
  card: { name: "Visa / Mastercard", color: "bg-slate-900 text-white" },
};

const amenityIcon = (name) => {
  const n = name.toLowerCase();
  if (n.includes("wi")) return Wifi;
  if (n.includes("breakfast") || n.includes("bbq")) return Coffee;
  if (n.includes("park")) return Car;
  if (n.includes("pool") || n.includes("lake")) return Waves;
  if (n.includes("kitchen")) return Utensils;
  return ShieldCheck;
};

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { format, currency } = useCurrency();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [duration, setDuration] = useState(2);
  const [checkIn, setCheckIn] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 2, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [keyType, setKeyType] = useState("digital");
  const [paymentMethod, setPaymentMethod] = useState("mtn");
  const [phone, setPhone] = useState("");
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    axios.get(`${API}/properties/${id}`).then(({ data }) => {
      setP(data);
      setKeyType(data.key_options?.[0] || "digital");
      setPaymentMethod(data.accepted_payments?.[0] || "mtn");
    });
    axios.get(`${API}/properties/${id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
  }, [id]);

  // Per-listing meta tags — LinkedIn + Google (both execute JS) will pick these
  // up so shares show the listing's own headline + hero photo. For FB/WhatsApp
  // (no JS), the /api/social/property/{id} endpoint returns pre-rendered OG HTML.
  useEffect(() => {
    if (!p) return;
    const prevTitle = document.title;
    document.title = `${p.title} · ${p.city} · EasyStay`;
    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    const rawPhoto = p.photos?.[0];
    const photo = rawPhoto
      ? (rawPhoto.startsWith("http") ? rawPhoto : `${API}/files/${rawPhoto}`)
      : "https://easystayzm.com/og-image.png";
    const desc = (p.description || "").slice(0, 180).replace(/\s+/g, " ").trim()
      || `Book this ${(p.type || "stay").replace("_", " ")} in ${p.city} on EasyStay — mobile money or card, digital or physical key.`;
    const url = `https://easystayzm.com/property/${p.id}`;
    setMeta("property", "og:title", `${p.title} · ${p.city}`);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:image", photo);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:title", `${p.title} · ${p.city}`);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", photo);
    return () => { document.title = prevTitle; };
  }, [p]);

  if (!p) return <div className="mx-auto max-w-7xl px-6 py-16 text-slate-500">{t("property.loading")}</div>;

  // Venue-style types (event centre, conference room) are booked strictly
  // by the hour — no nightly concept even for full-day bookings. Keep the
  // set in sync with `HOUR_ONLY_TYPES` in NewListing.jsx.
  const HOUR_ONLY_TYPES = new Set(["event_centre", "conference_room"]);
  const isHourOnly = HOUR_ONLY_TYPES.has(p?.type);
  const total = (!isHourOnly && duration >= 24)
    ? (duration / 24) * p.price_per_night_zmw
    : duration * p.price_per_hour_zmw;

  const handleBook = async () => {
    if (!user) {
      nav("/login", { state: { from: `/property/${p.id}` } });
      return;
    }
    if (duration < p.min_hours) {
      toast.error(t("property.booking.minHours", { n: p.min_hours }));
      return;
    }
    try {
      setBooking(true);
      const { data } = await axios.post(`${API}/bookings`, {
        property_id: p.id,
        check_in: new Date(checkIn).toISOString(),
        duration_hours: duration,
        payment_method: paymentMethod,
        currency_selected: currency,
        key_type: keyType,
        guest_phone: phone || null,
      });
      nav(`/booking/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create booking");
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
      {/* Gallery */}
      <div className="grid grid-cols-1 gap-3 overflow-hidden rounded-3xl md:grid-cols-4">
        {(p.photos || []).slice(0, 4).map((ph, i) => (
          <div key={i} className={`overflow-hidden ${i === 0 ? "md:col-span-2 md:row-span-2 aspect-[16/10] md:aspect-auto" : "aspect-[4/3]"}`}>
            <img src={photoSrc(ph)} alt="" className="h-full w-full object-cover" />
          </div>
        ))}
        {(!p.photos || p.photos.length === 0) && (
          <div className="col-span-full aspect-[16/9] bg-gradient-to-br from-copper/15 to-forest/15" />
        )}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2">
            <span className="chip">{p.type.replace("_", " ")}</span>
            {p.key_options?.map((k) => (
              <span key={k} className="chip !bg-copper/10 !text-copper"><KeyRound className="h-3 w-3" /> {t("property.keyLabel", { kind: k })}</span>
            ))}
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{p.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-slate-500">
            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {p.address}, {p.city}</span>
            {p.review_count > 0 && (
              <span data-testid="detail-rating-summary" className="flex items-center gap-1 font-semibold text-slate-700">
                <Star className="h-4 w-4 fill-copper text-copper" />
                {p.avg_rating?.toFixed?.(1) ?? p.avg_rating}
                <span className="font-normal text-slate-500">· {p.review_count} review{p.review_count === 1 ? "" : "s"}</span>
              </span>
            )}
            <FavouriteButton propertyId={p.id} initial={p.is_favourite} variant="solid" />
          </div>

          {p.host_name && (
            <div className="mt-4 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/70 px-4 py-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-copper/10 text-copper font-semibold">
                {p.host_name.slice(0, 1).toUpperCase()}
              </div>
              <div className="flex flex-1 flex-col">
                <div className="flex flex-wrap items-center gap-1 text-sm font-semibold text-slate-900">
                  {t("property.hostedBy", { name: p.host_name })}
                  {p.host_verified && (
                    <BadgeCheck
                      data-testid="verified-host-badge-detail"
                      className="h-4 w-4 fill-copper text-white"
                      aria-label="Verified host"
                    />
                  )}
                  {p.host_is_superhost && (
                    <span
                      data-testid="superhost-badge-detail"
                      className="ml-1 inline-flex items-center gap-1 rounded-full bg-forest px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                      title={t("property.superHost", { rating: p.host_avg_rating?.toFixed?.(1), count: p.host_review_count })}
                    >
                      <Crown className="h-3 w-3" /> {t("property.superhostBadge")}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {p.host_is_superhost
                    ? t("property.superHost", { rating: p.host_avg_rating?.toFixed?.(1), count: p.host_review_count })
                    : p.host_verified ? t("property.verifiedHost") : t("property.newHost")}
                </div>
              </div>
            </div>
          )}

          <p className="mt-6 text-slate-700 leading-relaxed">{p.description}</p>

          <div className="mt-8">
            <h3 className="font-display text-xl font-bold text-slate-900">{t("property.amenities")}</h3>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
              {p.amenities?.map((a) => {
                const Ic = amenityIcon(a);
                return (
                  <div key={a} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    <Ic className="h-4 w-4 text-forest" /> {a}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-8 rounded-3xl bg-white p-6 card-shadow">
            <h3 className="font-display text-lg font-bold text-slate-900">{t("property.payments")}</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {p.accepted_payments?.map((m) => (
                <span key={m} className={`rounded-full px-3 py-1 text-xs font-semibold ${paymentLabels[m]?.color || "bg-slate-100"}`}>{paymentLabels[m]?.name || m}</span>
              ))}
            </div>
          </div>

          {/* Reviews */}
          <div className="mt-8" data-testid="property-reviews-section">
            <h3 className="font-display text-xl font-bold text-slate-900">
              {reviews.length > 0 ? (
                <span className="flex items-center gap-2">
                  <Star className="h-5 w-5 fill-copper text-copper" />
                  {t("property.reviewsSummary", { rating: p.avg_rating?.toFixed?.(1), count: reviews.length })}
                </span>
              ) : t("property.reviewsHeading")}
            </h3>
            {reviews.length === 0 ? (
              <div className="mt-4 rounded-3xl bg-white p-6 text-sm text-slate-500 card-shadow">
                {t("property.reviewsEmpty")}
              </div>
            ) : (
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {reviews.slice(0, 8).map((r) => (
                  <div key={r.id} data-testid={`review-${r.id}`} className="rounded-3xl bg-white p-5 card-shadow">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-copper/10 text-copper font-semibold">
                        {(r.guest_name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-900">{r.guest_name}</div>
                        <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short" })}</div>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-copper text-copper" : "text-slate-200"}`} />
                        ))}
                      </div>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-slate-700">{r.comment}</p>
                    {r.owner_reply && (
                      <div data-testid={`review-reply-${r.id}`} className="mt-3 rounded-2xl border-l-2 border-copper bg-copper/5 p-4">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-copper">
                          <MessageSquare className="h-3.5 w-3.5" />
                          {r.owner_reply_name || p.host_name || "Host"} · reply
                        </div>
                        <p className="mt-1 text-sm leading-relaxed text-slate-700">{r.owner_reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Booking Card */}
        <div className="lg:sticky lg:top-24 h-fit">
          <div className="rounded-3xl bg-white p-6 card-shadow border border-slate-100">
            <div className="flex items-baseline gap-2">
              <div className="font-display text-3xl font-bold text-slate-900">{format(p.price_per_hour_zmw)}</div>
              <div className="text-sm text-slate-500">{t("property.booking.perHour")}</div>
              {!isHourOnly && (
                <div className="ml-auto text-xs text-slate-500">{t("property.booking.perNight", { price: format(p.price_per_night_zmw) })}</div>
              )}
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">{t("property.booking.checkIn")}</label>
                <input
                  data-testid="booking-checkin-input"
                  type="datetime-local"
                  value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">{t("property.booking.duration", { min: p.min_hours })}</label>
                <div className="mt-1 flex items-center gap-2">
                  <button type="button" onClick={() => setDuration(Math.max(p.min_hours, duration - 1))} className="h-10 w-10 rounded-full border border-slate-200 text-slate-700 hover:border-copper hover:text-copper">−</button>
                  <input
                    data-testid="booking-duration-input"
                    type="number" min={p.min_hours} value={duration}
                    onChange={(e) => setDuration(Math.max(p.min_hours, parseInt(e.target.value) || p.min_hours))}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-center text-sm outline-none focus:border-copper focus:bg-white"
                  />
                  <button type="button" onClick={() => setDuration(duration + 1)} className="h-10 w-10 rounded-full border border-slate-200 text-slate-700 hover:border-copper hover:text-copper">+</button>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {[2, 4, 8, 12, 24, 48].filter((h) => h >= p.min_hours).map((h) => (
                    <button type="button" key={h} onClick={() => setDuration(h)} className={`rounded-full px-3 py-1 text-xs font-medium ${duration === h ? "bg-forest text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{h >= 24 ? t("property.booking.days", { count: h/24 }) : t("property.booking.hours", { count: h })}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">{t("property.booking.keyDelivery")}</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {p.key_options?.map((k) => (
                    <button
                      type="button" key={k} data-testid={`key-${k}-btn`}
                      onClick={() => setKeyType(k)}
                      className={`rounded-xl border px-3 py-2.5 text-sm font-medium capitalize ${keyType === k ? "border-copper bg-copper/5 text-copper" : "border-slate-200 text-slate-700 hover:border-copper"}`}
                    >
                      <KeyRound className="mr-1 inline h-4 w-4" /> {k}
                    </button>
                  ))}
                </div>
                {keyType === "physical" && p.physical_key_address && (
                  <div className="mt-2 rounded-lg bg-forest/5 px-3 py-2 text-xs text-forest">{t("property.booking.collectAt", { addr: p.physical_key_address })}</div>
                )}
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">{t("property.booking.paymentMethod")}</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {p.accepted_payments?.map((m) => (
                    <button
                      type="button" key={m} data-testid={`pay-${m}-btn`}
                      onClick={() => setPaymentMethod(m)}
                      className={`rounded-xl border px-3 py-2.5 text-xs font-semibold ${paymentMethod === m ? "border-copper bg-copper/5 text-copper" : "border-slate-200 text-slate-700 hover:border-copper"}`}
                    >
                      {paymentLabels[m]?.name || m}
                    </button>
                  ))}
                </div>
              </div>

              {["mtn","airtel","zamtel"].includes(paymentMethod) && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">{t("property.booking.momoNumber")}</label>
                  <input
                    data-testid="booking-phone-input"
                    type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                    placeholder="+260 977 000 000"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
                  />
                </div>
              )}

              <div className="rounded-2xl bg-sand p-4">
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span>{t("property.booking.totalLine", {
                    price: format((!isHourOnly && duration >= 24) ? p.price_per_night_zmw : p.price_per_hour_zmw),
                    units: (!isHourOnly && duration >= 24) ? t("property.booking.nights", { count: duration/24 }) : t("property.booking.hoursUnit", { count: duration }),
                  })}</span>
                  <span>{format(total)}</span>
                </div>
                <div className="mt-2 flex items-center justify-between font-display text-xl font-bold text-slate-900">
                  <span>{t("property.booking.total")}</span>
                  <span data-testid="booking-total">{format(total)}</span>
                </div>
              </div>

              {user?.is_verified && (
                <div className="flex items-center gap-1 text-xs text-forest">
                  <BadgeCheck className="h-4 w-4 fill-copper text-white" /> {t("property.booking.verifiedGuest")}
                </div>
              )}

              <button
                data-testid="booking-confirm-btn"
                onClick={handleBook} disabled={booking}
                className="btn-primary w-full text-sm"
              >
                {booking ? t("property.booking.creating") : t("property.booking.continue")}
              </button>

              <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5" /> {t("property.booking.notCharged")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
